import { useMemo } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { CheckmarkCircle16Filled } from "@fluentui/react-icons";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  EMP_FUNCTIONS,
  EMP_TYPES,
  EMP_TYPE_LABELS,
  EMP_BOOLEAN_COLS,
  EMP_BOOLEAN_LABELS,
  EMP_BOOLEAN_SHORT,
  EMP_FLAGS_KEY,
  type EmpType,
  type EmpBoolCol,
} from "./schema";

type Matrix = Record<string, Record<string, number>>;
type FlagMatrix = Record<string, Partial<Record<EmpBoolCol, boolean>>>;

type ParsedMatrix = { counts: Matrix; flags: FlagMatrix };

const parseMatrix = (raw: string | undefined): ParsedMatrix => {
  if (!raw) return { counts: {}, flags: {} };
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return { counts: {}, flags: {} };
    const { [EMP_FLAGS_KEY]: flagsRaw, ...rest } = parsed as Record<string, unknown>;
    const counts = rest as Matrix;
    const flags = (typeof flagsRaw === "object" && flagsRaw !== null ? flagsRaw : {}) as FlagMatrix;
    return { counts, flags };
  } catch {
    return { counts: {}, flags: {} };
  }
};

const serializeMatrix = (counts: Matrix, flags: FlagMatrix): string => {
  const cleanFlags: FlagMatrix = {};
  for (const [func, cols] of Object.entries(flags)) {
    const kept: Partial<Record<EmpBoolCol, boolean>> = {};
    for (const k of EMP_BOOLEAN_COLS) {
      const v = cols?.[k];
      if (v === true || v === false) kept[k] = v;
    }
    if (Object.keys(kept).length > 0) cleanFlags[func] = kept;
  }
  const payload: Record<string, unknown> = { ...counts };
  if (Object.keys(cleanFlags).length > 0) payload[EMP_FLAGS_KEY] = cleanFlags;
  return JSON.stringify(payload);
};

const useStyles = makeStyles({
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    overflowX: "auto",
  },
  table: {
    width: "100%",
    minWidth: "640px",
    borderCollapse: "collapse",
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
  },
  th: {
    fontWeight: 700,
    color: BRAND.ink,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: BRAND.bg,
    textAlign: "center",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  thFunc: {
    textAlign: "left",
    paddingLeft: "16px",
    minWidth: "180px",
  },
  thHover: {
    fontFamily: BRAND.fontAccent,
    color: BRAND.purple,
  },
  thHoverHighlight: {
    backgroundColor: "#f5eef6",
  },
  funcCell: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "16px",
    paddingRight: "8px",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  totalCell: {
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "12px",
    textAlign: "center",
    fontFamily: BRAND.fontAccent,
    fontWeight: 600,
    color: BRAND.purple,
    backgroundColor: BRAND.bg,
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  cell: {
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "4px",
    paddingRight: "4px",
    textAlign: "center",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  input: {
    width: "60px",
    boxSizing: "border-box",
    textAlign: "center",
    fontFamily: BRAND.fontPrimary,
    fontVariantNumeric: "tabular-nums",
    fontSize: "1.05rem",
    color: BRAND.ink,
    backgroundColor: "transparent",
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "4px",
    paddingRight: "4px",
    ...shorthands.border("1px", "solid", "transparent"),
    ...shorthands.borderRadius("4px"),
    outlineWidth: 0,
    transitionProperty: "border-color, background-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor("#d4cfd9"),
    },
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f5eef6",
    },
  },
  footerRow: {
    backgroundColor: BRAND.bg,
  },
  footerLabel: {
    fontFamily: BRAND.fontAccent,
    fontWeight: 600,
    color: BRAND.purple,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
  },
  footerCell: {
    fontFamily: BRAND.fontAccent,
    fontWeight: 600,
    color: BRAND.purple,
    paddingTop: "10px",
    paddingBottom: "10px",
    textAlign: "center",
  },
  legend: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "16px",
    rowGap: "6px",
    paddingTop: "12px",
    paddingBottom: "0",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  legendKey: {
    fontWeight: 700,
    color: BRAND.ink,
    marginRight: "4px",
  },
  flagToggle: {
    display: "inline-flex",
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
    overflow: "hidden",
  },
  flagBtn: {
    appearance: "none",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: BRAND.muted,
    backgroundColor: "transparent",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.border("0"),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "32px",
    ":hover": {
      backgroundColor: "#f5eef6",
    },
  },
  flagBtnYes: {
    backgroundColor: "#e7f6ec",
    color: "#0e7c3a",
    ":hover": {
      backgroundColor: "#d7eedf",
    },
  },
  flagBtnNo: {
    backgroundColor: "#fdecec",
    color: "#b91c1c",
    ":hover": {
      backgroundColor: "#f9dada",
    },
  },
});

type Props = {
  value: string | undefined;
  onChange: (next: string) => void;
};

const EmployeeMatrixGrid = ({ value, onChange }: Props) => {
  const styles = useStyles();
  const { counts, flags } = useMemo(() => parseMatrix(value), [value]);

  const setCell = (func: string, type: EmpType, raw: string) => {
    const nextCounts: Matrix = { ...counts, [func]: { ...(counts[func] ?? {}) } };
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      nextCounts[func][type] = parsed;
    } else {
      delete nextCounts[func][type];
      if (Object.keys(nextCounts[func]).length === 0) delete nextCounts[func];
    }
    onChange(serializeMatrix(nextCounts, flags));
  };

  const setFlag = (func: string, col: EmpBoolCol, val: boolean | null) => {
    const nextFlags: FlagMatrix = { ...flags, [func]: { ...(flags[func] ?? {}) } };
    if (val === null) {
      delete nextFlags[func][col];
      if (Object.keys(nextFlags[func]).length === 0) delete nextFlags[func];
    } else {
      nextFlags[func][col] = val;
    }
    onChange(serializeMatrix(counts, nextFlags));
  };

  const cellVal = (func: string, type: EmpType): string => {
    const v = counts[func]?.[type];
    return v && v > 0 ? String(v) : "";
  };

  const flagVal = (func: string, col: EmpBoolCol): boolean | undefined => flags[func]?.[col];

  const rowTotal = (func: string): number =>
    EMP_TYPES.reduce((sum, t) => sum + (counts[func]?.[t] ?? 0), 0);

  const colTotal = (type: EmpType): number =>
    EMP_FUNCTIONS.reduce((sum, f) => sum + (counts[f]?.[type] ?? 0), 0);

  const grandTotal = EMP_FUNCTIONS.reduce((s, f) => s + rowTotal(f), 0);

  return (
    <div>
      <div className={styles.card}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={mergeClasses(styles.th, styles.thFunc)}>Funktion</th>
              {EMP_TYPES.map((t) => (
                <th key={t} className={mergeClasses(styles.th, styles.thHover)} title={EMP_TYPE_LABELS[t]}>
                  {t}
                </th>
              ))}
              <th className={mergeClasses(styles.th, styles.thHover)}>Σ</th>
              {EMP_BOOLEAN_COLS.map((c) => (
                <th
                  key={c}
                  className={mergeClasses(styles.th, styles.thHover)}
                  title={EMP_BOOLEAN_LABELS[c]}
                >
                  {EMP_BOOLEAN_SHORT[c]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EMP_FUNCTIONS.map((f) => (
              <tr key={f}>
                <td className={styles.funcCell}>{f}</td>
                {EMP_TYPES.map((t) => (
                  <td key={t} className={styles.cell}>
                    <input
                      className={styles.input}
                      value={cellVal(f, t)}
                      onChange={(e) => setCell(f, t, e.target.value)}
                      inputMode="numeric"
                    />
                  </td>
                ))}
                <td className={styles.totalCell}>
                  {rowTotal(f) || ""}
                </td>
                {EMP_BOOLEAN_COLS.map((c) => {
                  const v = flagVal(f, c);
                  return (
                    <td key={c} className={styles.cell}>
                      <div className={styles.flagToggle}>
                        <button
                          type="button"
                          className={mergeClasses(styles.flagBtn, (v === true) && styles.flagBtnYes)}
                          onClick={() => setFlag(f, c, v === true ? null : true)}
                          title={`${EMP_BOOLEAN_LABELS[c]}: ja`}
                        >
                          {v === true ? <CheckmarkCircle16Filled /> : "ja"}
                        </button>
                        <button
                          type="button"
                          className={mergeClasses(styles.flagBtn, (v === false) && styles.flagBtnNo)}
                          onClick={() => setFlag(f, c, v === false ? null : false)}
                          title={`${EMP_BOOLEAN_LABELS[c]}: nein`}
                        >
                          nein
                        </button>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className={styles.footerRow}>
              <td className={styles.footerLabel}>Σ pro Typ</td>
              {EMP_TYPES.map((t) => (
                <td key={t} className={styles.footerCell}>
                  {colTotal(t) || ""}
                </td>
              ))}
              <td className={styles.footerCell}>{grandTotal || ""}</td>
              {EMP_BOOLEAN_COLS.map((c) => (
                <td key={c} className={styles.footerCell} />
              ))}
            </tr>
          </tbody>
        </table>
        <div className={styles.legend}>
          {EMP_TYPES.map((t) => (
            <span key={t}>
              <span className={styles.legendKey}>{t}</span>
              {EMP_TYPE_LABELS[t]}
            </span>
          ))}
          {EMP_BOOLEAN_COLS.map((c) => (
            <span key={c}>
              <span className={styles.legendKey}>{EMP_BOOLEAN_SHORT[c]}</span>
              {EMP_BOOLEAN_LABELS[c]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMatrixGrid;
